/**
 * App notifications in the browser: registers the service worker (sw.js), asks permission and
 * hands the browser's push subscription to the server, which sends alerts to it.
 *
 * iPhone: Safari only allows notifications for a site added to the Home Screen and opened from
 * there (iOS 16.4+). pushSupport() says which case applies so the screen can explain.
 */
import { api } from "./api.ts";

export type PushSupport = "yes" | "ios-home-screen" | "no";

export function pushSupport(): PushSupport {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const has = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (ios && !standalone) return "ios-home-screen";
  return has ? "yes" : "no";
}

function keyBytes(base64url: string): Uint8Array {
  const s = atob(base64url.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((base64url.length + 3) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration("/")) ?? navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

/** This device's subscription, if notifications are on here. */
export async function currentPush(): Promise<PushSubscription | null> {
  if (pushSupport() !== "yes") return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Turns notifications on for this device (FMS staff: for the farm they're looking at). */
export async function turnPushOn(accountId?: string): Promise<PushSubscription> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "Notifications are blocked for this site in the browser's settings." : "Notifications weren't allowed.");
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await api.pushKey();
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource }));
  await api.pushOn(sub.toJSON(), accountId);
  return sub;
}

export async function turnPushOff(): Promise<void> {
  const sub = await currentPush();
  if (!sub) return;
  await api.pushOff(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe();
}
